import React, { Component } from 'react';
import Layout from '../Components/Layout';
import axios from 'axios';
import './AllJobs.css';

class AllJobs extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            jobs: [],
            searchText: '',
            dealTypeFilter: [],
            showDealTypeDropdown: false,
            currentPage: 1,
            itemsPerPage: 10,
            totalItems: 0
        };
        this.searchTimeout = null;
        this.dealTypes = [
            'Sales Order (SO)',
            'Invoice',
            'Lost (Dead)',
            'SO Cancelled',
            'Quotation/Estimation',
            'Rework',
            'PO Accepted'
        ];
        this.dealTypeDropdownRef = React.createRef();
    }

    componentDidMount() {
        // Load data on initial page load
        this.loadAllJobs();
        // Add click listener to close dropdown when clicking outside
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        // Clean up timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        // Remove click listener
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event) => {
        if (this.dealTypeDropdownRef.current && !this.dealTypeDropdownRef.current.contains(event.target)) {
            this.setState({ showDealTypeDropdown: false });
        }
    }

    loadAllJobs = async (searchText = '', dealTypeFilters = [], page = 1) => {
        try {
            let url = '/rfq/all-jobs';
            const params = [];

            if (searchText && searchText.trim() !== '') {
                params.push(`search=${encodeURIComponent(searchText.trim())}`);
            }

            if (dealTypeFilters && dealTypeFilters.length > 0) {
                dealTypeFilters.forEach(dealType => {
                    params.push(`dealType=${encodeURIComponent(dealType)}`);
                });
            }

            params.push(`page=${page}`);
            params.push(`limit=${this.state.itemsPerPage}`);

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await axios.get(url);
            if (response.data) {
                this.setState({
                    jobs: response.data.items,
                    totalItems: response.data.total,
                    currentPage: page,
                    loading: false
                });
            }
        } catch (error) {
            console.error('Error loading all jobs:', error);
            this.setState({ loading: false });
        }
    }

    handleSearchChange = (e) => {
        const searchText = e.target.value;
        this.setState({ searchText });

        // Clear any existing timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        // Set a new timeout to trigger search after user stops typing
        this.searchTimeout = setTimeout(() => {
            this.setState({ loading: true, currentPage: 1 }, () => {
                this.loadAllJobs(searchText, this.state.dealTypeFilter, 1);
            });
        }, 500); // Wait 500ms after user stops typing
    }

    handleSearchClear = () => {
        this.setState({ searchText: '', loading: true, currentPage: 1 }, () => {
            this.loadAllJobs('', this.state.dealTypeFilter, 1);
        });
    }

    handleDealTypeToggle = (dealType) => {
        const { dealTypeFilter } = this.state;
        let newFilters;

        if (dealTypeFilter.includes(dealType)) {
            // Remove the deal type
            newFilters = dealTypeFilter.filter(dt => dt !== dealType);
        } else {
            // Add the deal type
            newFilters = [...dealTypeFilter, dealType];
        }

        this.setState({ dealTypeFilter: newFilters, loading: true, currentPage: 1 }, () => {
            this.loadAllJobs(this.state.searchText, newFilters, 1);
        });
    }

    toggleDealTypeDropdown = () => {
        this.setState(prevState => ({ showDealTypeDropdown: !prevState.showDealTypeDropdown }));
    }

    clearAllDealTypes = () => {
        this.setState({ dealTypeFilter: [], loading: true, currentPage: 1 }, () => {
            this.loadAllJobs(this.state.searchText, [], 1);
        });
    }

    handlePageChange = (page) => {
        this.setState({ loading: true }, () => {
            this.loadAllJobs(this.state.searchText, this.state.dealTypeFilter, page);
        });
    }

    getStatusLabel = (status) => {
        const statusMap = {
            1: 'RFQ',
            2: 'R&D',
            3: 'Draft - Purch.Review',
            4: 'Draft - Acct.Mgmt.Review',
            5: 'Draft - Mgmt.Review',
            6: 'Final Quote',
            7: 'Quote Submitted'
        };
        return statusMap[status] || 'Unknown';
    }

    formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    renderPagination = () => {
        const { currentPage, totalItems, itemsPerPage } = this.state;
        const totalPages = Math.ceil(totalItems / itemsPerPage);

        if (totalPages <= 1) return null;

        const pages = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <div className="pagination-container">
                <button
                    className="pagination-btn"
                    onClick={() => this.handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>

                {startPage > 1 && (
                    <>
                        <button className="pagination-btn" onClick={() => this.handlePageChange(1)}>1</button>
                        {startPage > 2 && <span className="pagination-ellipsis">...</span>}
                    </>
                )}

                {pages.map(page => (
                    <button
                        key={page}
                        className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => this.handlePageChange(page)}
                    >
                        {page}
                    </button>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
                        <button className="pagination-btn" onClick={() => this.handlePageChange(totalPages)}>
                            {totalPages}
                        </button>
                    </>
                )}

                <button
                    className="pagination-btn"
                    onClick={() => this.handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
            </div>
        );
    }

    render() {
        const { loading, jobs, searchText, dealTypeFilter, showDealTypeDropdown, totalItems, currentPage, itemsPerPage } = this.state;

        return (
            <Layout>
                <div className="all-jobs-container">
                    <div className="all-jobs-header">
                        <p className="all-jobs-title">View and filter all your jobs by item number and deal type</p>
                        <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                            <div style={{position: 'relative', display: 'flex', alignItems: 'center'}}>
                                <i className="ti-search" style={{position: 'absolute', left: '12px', color: '#999', fontSize: '14px'}}></i>
                                <input
                                    type="text"
                                    placeholder="Search by Item Number, Product Name, or Customer..."
                                    value={searchText}
                                    onChange={this.handleSearchChange}
                                    style={{
                                        padding: '8px 35px 8px 35px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        width: '350px'
                                    }}
                                />
                                {searchText && (
                                    <i
                                        className="ti-close"
                                        onClick={this.handleSearchClear}
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            color: '#999',
                                            fontSize: '12px',
                                            cursor: 'pointer'
                                        }}
                                    ></i>
                                )}
                            </div>
                            <div style={{position: 'relative'}} ref={this.dealTypeDropdownRef}>
                                <button
                                    onClick={this.toggleDealTypeDropdown}
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        backgroundColor: 'white',
                                        cursor: 'pointer',
                                        minWidth: '200px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '10px'
                                    }}
                                >
                                    <span>
                                        {dealTypeFilter.length === 0
                                            ? 'All Deal Types'
                                            : `${dealTypeFilter.length} selected`}
                                    </span>
                                    <i className={`ti-angle-${showDealTypeDropdown ? 'up' : 'down'}`} style={{fontSize: '12px'}}></i>
                                </button>
                                {showDealTypeDropdown && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        marginTop: '4px',
                                        backgroundColor: 'white',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        zIndex: 1000,
                                        minWidth: '200px',
                                        maxHeight: '300px',
                                        overflowY: 'auto'
                                    }}>
                                        <div style={{padding: '8px', borderBottom: '1px solid #e0e0e0'}}>
                                            <button
                                                onClick={this.clearAllDealTypes}
                                                style={{
                                                    padding: '4px 8px',
                                                    border: 'none',
                                                    background: 'none',
                                                    color: '#007bff',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    width: '100%',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                        {this.dealTypes.map(dealType => (
                                            <label
                                                key={dealType}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    gap: '8px',
                                                    borderBottom: '1px solid #f0f0f0'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={dealTypeFilter.includes(dealType)}
                                                    onChange={() => this.handleDealTypeToggle(dealType)}
                                                    style={{cursor: 'pointer'}}
                                                />
                                                <span>{dealType}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="all-jobs-content">
                        {loading ? (
                            <div className="loading-message">Loading...</div>
                        ) : (
                            <div className="jobs-table-container">
                                <table className="jobs-table">
                                    <thead>
                                        <tr>
                                            <th>Item Number</th>
                                            <th>Product Name</th>
                                            <th>Customer</th>
                                            <th>Deal Type</th>
                                            <th>Status</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {jobs.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="no-data">No jobs found</td>
                                            </tr>
                                        ) : (
                                            jobs.map((job, index) => (
                                                <tr key={index}>
                                                    <td>{job.itemNumber || 'N/A'}</td>
                                                    <td>{job.productName || 'N/A'}</td>
                                                    <td>{job.customer || 'N/A'}</td>
                                                    <td>{job.dealType || 'N/A'}</td>
                                                    <td>
                                                        <span className={`status-badge status-${job.status}`}>
                                                            {this.getStatusLabel(job.status)}
                                                        </span>
                                                    </td>
                                                    <td>{this.formatDate(job.date)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {!loading && jobs.length > 0 && (
                            <div className="pagination-info">
                                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} jobs
                            </div>
                        )}
                        {this.renderPagination()}
                    </div>
                </div>
            </Layout>
        );
    }
}

export default AllJobs;
